import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/useAuth'
import { CellarContext } from '@/contexts/cellar-context'
import type { CellarSummary } from '@/contexts/cellar-context'

const STORAGE_KEY = 'cellarwise-selected-cellar'

export function CellarProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [cellars, setCellars] = useState<CellarSummary[]>([])
  const [cellarId, setCellarId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let cancelled = false

    if (!user) {
      Promise.resolve().then(() => {
        if (!cancelled) {
          setCellars([])
          setCellarId(null)
          setLoading(false)
        }
      })
      return () => { cancelled = true }
    }

    Promise.resolve(
      supabase
        .from('cellars')
        .select('id, name')
        .eq('user_id', user.id)
        .order('created_at')
    ).then(({ data }) => {
      if (!cancelled) {
        const list = (data ?? []) as CellarSummary[]
        setCellars(list)

        if (list.length === 0) {
          setCellarId(null)
        } else {
          // Restore last-selected cellar from localStorage
          const saved = localStorage.getItem(STORAGE_KEY)
          const match = list.find(c => c.id === saved)
          setCellarId(match ? match.id : list[0].id)
        }
        setLoading(false)
      }
    }).catch(() => {
      if (!cancelled) {
        setLoading(false)
      }
    })

    return () => { cancelled = true }
  }, [user, version])

  const refresh = useCallback(() => {
    setVersion((v) => v + 1)
  }, [])

  const selectCellar = useCallback((id: string) => {
    setCellarId(id)
    localStorage.setItem(STORAGE_KEY, id)
  }, [])

  const cellarName = cellars.find(c => c.id === cellarId)?.name ?? null

  return (
    <CellarContext.Provider
      value={{
        cellars,
        cellarId,
        cellarName,
        hasCellar: cellars.length > 0,
        loading,
        refresh,
        selectCellar,
      }}
    >
      {children}
    </CellarContext.Provider>
  )
}
